<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import Loader from "@/components/widgets/preloader";
import { mapGetters, mapActions } from "vuex";
import { ModelListSelect } from "vue-search-select";
import moment from "moment";
import axios from "axios";

export default {
  components: { Layout, PageHeader, Loader, ModelListSelect },

  data() {
    return {
      title: "INCOME STATEMENT",

      expanded: {
        revenue: false,
        returns: false,
        cost: false,
        expenses: false,
        other: false,
      },

      filterData: {
        time: "annual",
        datefrom: new Date(new Date().getFullYear(), 0, 1).toLocaleDateString("en-CA"),
        dateto: moment().format("YYYY-MM-DD"),
        year: new Date().getFullYear(),
        branch_id: 0,
      },

      dashboardLabel: new Date().getFullYear() + "'s",

      timeFilters: [
        { title: "Annual", value: "annual" },
        { title: "This month", value: "month" },
        { title: "This quarter", value: "quarter" },
        { title: "Customize", value: "custom" },
      ],
    };
  },

  computed: {
    ...mapGetters("coa", [
      "IncomeStatement",
      "RevenueDetails",
      "CostofsalesDetails",
      "ExpenseDetails",
      "OtherincomeDetails",
      "SaleReturnsDetails",
      "filterStatus",
    ]),

    grossProfit() {
      if (!this.IncomeStatement) return 0;
      return this.IncomeStatement.sales -
        (this.IncomeStatement.costofsales + this.IncomeStatement.saleReturns);
    },

    operatingProfit() {
      if (!this.IncomeStatement) return 0;
      return (
        this.IncomeStatement.sales +
        this.IncomeStatement.otherincome -
        (this.IncomeStatement.expenses +
          this.IncomeStatement.costofsales +
          this.IncomeStatement.saleReturns)
      );
    },
  },

  methods: {
    ...mapActions({
      FetchStatement: "coa/fetchIncomestatement",
      FetchRevenue: "coa/fetchRevenueDetails",
      FetchCostofSales: "coa/fetchCostofsalesDetails",
      FetchExpenses: "coa/fetchExpenseDetails",
      FetchOtherincome: "coa/fetchOtherincomeDetails",
      FetchSaleReturns: "coa/fetchSaleReturnsDetails",
    }),

    format(val) {
      return "UGX " + Number(val || 0).toLocaleString();
    },

    profitClass(val) {
      return val >= 0 ? "text-success" : "text-danger";
    },

    toggle(section) {
      this.expanded[section] = !this.expanded[section];

      if (this.expanded[section]) {
        const map = {
          revenue: this.FetchRevenue,
          returns: this.FetchSaleReturns,
          cost: this.FetchCostofSales,
          expenses: this.FetchExpenses,
          other: this.FetchOtherincome,
        };
        if (map[section]) map[section](this.filterData);
      }
    },

    setLabel() {
      const f = this.filterData;
      if (f.time === "annual") this.dashboardLabel = f.year + "'s";
      if (f.time === "month") this.dashboardLabel = "This month's";
      if (f.time === "quarter") this.dashboardLabel = "This quarter's";
      if (f.time === "custom") this.dashboardLabel = f.datefrom + " to " + f.dateto;
    },

    FetchIncomeStatement() {
      this.FetchStatement(this.filterData);
    },

    // ✅ UPDATED: browser print
    printPDF() {
      window.print();
    },

    Excel_export() {
      axios.post("export_is", this.filterData, { responseType: "arraybuffer" })
        .then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "income_statement.xlsx");
          link.click();
        });
    },
  },

  created() {
    this.FetchStatement(this.filterData);
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" />

    <!-- ✅ HIDE THIS WHOLE BLOCK IN PRINT -->
    <div class="card p-4 no-print">

      <!-- FILTERS -->
      <div class="row mb-4 align-items-end">
        <div class="col-md-2">
          <label>Period</label>
          <model-list-select :list="timeFilters" v-model="filterData.time" option-value="value" option-text="title"
            @input="setLabel(), FetchIncomeStatement()" />
        </div>

        <div class="col-md-2" v-if="filterData.time == 'annual'">
          <label>Year</label>
          <input class="form-control" v-model="filterData.year" @keyup.enter="setLabel(), FetchIncomeStatement()"/>
        </div>

        <div class="col-md-2" v-if="filterData.time == 'custom'">
          <label>From</label>
          <input type="date" class="form-control" v-model="filterData.datefrom" />
        </div>

        <div class="col-md-2" v-if="filterData.time == 'custom'">
          <label>To</label>
          <input type="date" class="form-control" v-model="filterData.dateto" />
        </div>

        <div class="col-md-2">
          <button class="btn btn-warning w-100" @click="FetchIncomeStatement">
            <span class="fa fa-filter"></span> Filter
            <b-spinner v-show="filterStatus" small></b-spinner>
          </button>
        </div>

        <div class="col-md-4 text-end">
          <button class="btn btn-info me-2" @click="Excel_export"><i class="fa fa-file-excel"></i> Export</button>
          <button class="btn btn-dark" @click="printPDF"><i class="fa fa-print"></i> Print</button>
        </div>
      </div>

    </div>

    <!-- ✅ PRINTABLE CONTENT -->
    <div id="printArea" class="card p-4">

      <h2 class="text-center fw-bold mb-4 big-title">
        {{ dashboardLabel }} INCOME STATEMENT
      </h2>

      <div v-if="IncomeStatement">

        <!-- REVENUE -->
        <div class="row-line">
          <span @click="toggle('revenue')" class="section-title">
            <i :class="expanded.revenue ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            Revenue
          </span>
          <span>{{ format(IncomeStatement.sales) }}</span>
        </div>

        <div v-if="expanded.revenue" class="details-block">
          <div v-if="filterStatus"><i class="fa fa-spinner fa-spin"></i> Loading...</div>
          <div v-else-if="RevenueDetails && RevenueDetails.length">
            <div v-for="(item, i) in RevenueDetails" :key="i" class="detail-row">
              <span>- {{ item.name }}</span>
              <span>{{ format(item.amount) }}</span>
            </div>
          </div>
          <div v-else>No data</div>
        </div>

        <!-- RETURNS -->
        <div class="row-line">
          <span @click="toggle('returns')" class="section-title">
            <i :class="expanded.returns ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            Less: Sales Returns
          </span>
          <span>{{ format(IncomeStatement.saleReturns) }}</span>
        </div>

        <!-- COST -->
        <div class="row-line">
          <span @click="toggle('cost')" class="section-title">
            <i :class="expanded.cost ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            Cost of Goods Sold
          </span>
          <span>{{ format(IncomeStatement.costofsales) }}</span>
        </div>

        <!-- GROSS -->
        <div class="row-total">
          <span>Gross Profit</span>
          <span :class="profitClass(grossProfit)">
            {{ format(grossProfit) }}
          </span>
        </div>

        <!-- OTHER -->
        <div class="row-line">
          <span @click="toggle('other')" class="section-title">
            <i :class="expanded.other ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            Other Income
          </span>
          <span>{{ format(IncomeStatement.otherincome) }}</span>
        </div>

        <!-- EXPENSES -->
        <div class="row-line">
          <span @click="toggle('expenses')" class="section-title">
            <i :class="expanded.expenses ? 'fa fa-chevron-down' : 'fa fa-chevron-right'"></i>
            Operating Expenses
          </span>
          <span>{{ format(IncomeStatement.expenses) }}</span>
        </div>

        <!-- FINAL -->
        <div class="row-final">
          <span>Operating Profit</span>
          <span :class="profitClass(operatingProfit)">
            {{ format(operatingProfit) }}
          </span>
        </div>

      </div>

      <Loader v-else />

    </div>
  </Layout>
</template>

<style>
.big-title {
  font-size: 32px;
}

.row-line {
  display: flex;
  justify-content: space-between;
  font-size: 20px;
  padding: 12px 0;
}

.section-title {
  font-weight: 600;
  cursor: pointer;
}

.details-block {
  padding-left: 25px;
  margin-bottom: 10px;
  font-size: 17px;
  color: #495057;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
}

.row-total {
  font-size: 24px;
  font-weight: bold;
  background: #f8f9fa;
  padding: 14px;
  margin-top: 10px;
}

.row-final {
  font-size: 26px;
  font-weight: bold;
  background: #e9ecef;
  padding: 18px;
  margin-top: 15px;
}

/* ✅ PRINT SETTINGS */
@media print {

  /* Hide EVERYTHING */
  body * {
    visibility: hidden !important;
  }

  /* Show ONLY the report */
  #printArea,
  #printArea * {
    visibility: visible !important;
  }

  /* Position it properly */
  #printArea {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    padding: 10px;
  }

  /* Remove margins from browser */
  body {
    margin: 0;
  }
}
</style>